import { Controller } from "@hotwired/stimulus";
import {
  beginSkeletonWait,
  endSkeletonWait,
} from "../skeleton_wait_support";
import { matchesTurboFrameFetch } from "../table_frame_support";

export default class extends Controller {
  static targets = [
    "rowCheckbox",
    "selectAll",
    "exportButton",
    "exportCount",
    "totalCount",
    "exportModal",
    "studentCount",
    "confirmExportButton",
    "status",
  ];
  static values = {
    exportUrl: String,
    frameId: String,
    tableFrameId: String,
  };

  connect() {
    this.ensureSelectionState();
    this.tableBusy = false;
    this.boundFilterSubmit = this.beforeFilterSubmit.bind(this);
    this.boundFrameLoad = this.onFrameLoad.bind(this);
    this.element.addEventListener("turbo:submit-start", this.boundFilterSubmit);
    document.addEventListener("turbo:frame-load", this.boundFrameLoad);
    this.syncExportButton();
    this.syncSelectAllState();
  }

  disconnect() {
    if (this.boundFilterSubmit) {
      this.element.removeEventListener(
        "turbo:submit-start",
        this.boundFilterSubmit,
      );
    }
    if (this.boundFrameLoad) {
      document.removeEventListener("turbo:frame-load", this.boundFrameLoad);
    }
    this.endWait();
  }

  tableLoading(event) {
    if (
      event?.type === "turbo:before-fetch-request" &&
      !matchesTurboFrameFetch(event, [this.tableFrameIdValue])
    ) {
      return;
    }

    this.tableBusy = true;
    this.syncExportButton();
  }

  onFrameLoad(event) {
    if (event.target.id !== this.tableFrameIdValue) return;

    this.tableBusy = false;
    this.syncExportButton();
    this.maybeEndSkeletonWait();
  }

  rowCheckboxTargetConnected(checkbox) {
    this.ensureSelectionState();
    const row = this.rowPayload(checkbox);
    if (!row) return;

    checkbox.checked = this.isRowSelected(String(row.id));

    this.syncSelectAllState();
    this.syncExportButton();
  }

  totalCountTargetConnected() {
    this.ensureSelectionState();
    this.syncExportButton();
  }

  selectAllTargetConnected() {
    this.ensureSelectionState();
    this.syncSelectAllState();
  }

  toggleRow(event) {
    const checkbox = event.currentTarget;
    const row = this.rowPayload(checkbox);
    if (!row) return;

    const id = String(row.id);

    if (this.selectAllMode) {
      if (checkbox.checked) {
        this.excludedIds.delete(id);
      } else {
        this.excludedIds.add(id);
      }
    } else if (checkbox.checked) {
      this.selectedRows.set(id, row);
    } else {
      this.selectedRows.delete(id);
    }

    this.syncRowCheckboxStates();
    this.syncExportButton();
    this.syncSelectAllState();
  }

  toggleAll(event) {
    const checked = event.currentTarget.checked;

    if (checked) {
      this.selectAllMode = true;
      this.excludedIds.clear();
      this.selectedRows.clear();
    } else {
      this.clearSelection();
    }

    this.syncRowCheckboxStates();
    this.syncExportButton();
    this.syncSelectAllState();
  }

  beforeFilterSubmit(event) {
    const form = event.target;
    if (form?.tagName !== "FORM") return;

    const action = form.getAttribute("action") || "";
    if (!action.includes("student_activity_log")) return;

    this.clearSelection();
    this.syncRowCheckboxStates();
    this.syncExportButton();
    this.syncSelectAllState();
  }

  openExportModal(event) {
    event?.preventDefault();
    const count = this.selectionCount();
    if (count === 0 || this.exporting) return;

    if (this.hasStudentCountTarget) {
      this.studentCountTarget.textContent = String(count);
    }

    this.modalController()?.show(event);
  }

  async confirmExport(event) {
    event?.preventDefault();
    const count = this.selectionCount();
    if (count === 0 || this.exporting) return;

    const body = this.selectAllMode
      ? {
          select_all: true,
          excluded_ids: Array.from(this.excludedIds),
        }
      : {
          rows: Array.from(this.selectedRows.values()).map(({ id }) => ({
            id,
          })),
        };

    this.beginWait();

    try {
      const response = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        this.announce("Export could not be started. Try again.");
        return;
      }

      this.modalController()?.close(event);
      this.clearSelection();
      this.syncRowCheckboxStates();
      this.syncSelectAllState();
      this.syncExportButton();
      this.announce("Export process has started. Please wait for few minutes.");
    } catch {
      this.announce("Export could not be started. Try again.");
    } finally {
      this.endWait();
    }
  }

  syncExportButton() {
    if (!this.hasExportButtonTarget) return;

    const count = this.selectionCount();
    const isDisabled =
      count === 0 || Boolean(this.exporting) || Boolean(this.tableBusy);
    this.exportButtonTarget.disabled = isDisabled;
    if (isDisabled) {
      this.exportButtonTarget.setAttribute("aria-disabled", "true");
    } else {
      this.exportButtonTarget.removeAttribute("aria-disabled");
    }

    this.syncExportCount(count);
  }

  syncExportCount(count) {
    if (!this.hasExportCountTarget) return;

    const badge = this.exportCountTarget;
    if (count === 0) {
      badge.textContent = "";
      badge.classList.add("hidden");
      this.exportButtonTarget.setAttribute("aria-label", "Export");
      return;
    }

    const label = count === 1 ? "1 selected" : `${count} selected`;
    badge.textContent = String(count);
    badge.classList.remove("hidden");
    this.exportButtonTarget.setAttribute("aria-label", `Export, ${label}`);
  }

  syncSelectAllState() {
    this.ensureSelectionState();
    if (!this.hasSelectAllTarget || this.rowCheckboxTargets.length === 0)
      return;

    this.selectAllTargets.forEach((selectAll) => {
      if (this.selectAllMode) {
        const hasExclusions = this.excludedIds.size > 0;
        selectAll.checked = !hasExclusions;
        selectAll.indeterminate = hasExclusions;
        return;
      }

      const pageIds = this.pageRowIds();
      const selectedOnPageCount = [...pageIds].filter((id) =>
        this.selectedRows.has(id),
      ).length;
      const allOnPageSelected =
        selectedOnPageCount === pageIds.size && pageIds.size > 0;

      selectAll.checked = allOnPageSelected;
      selectAll.indeterminate =
        !allOnPageSelected && this.selectedRows.size > 0;
    });
  }

  pageRowIds() {
    const ids = new Set();
    this.rowCheckboxTargets.forEach((checkbox) => {
      const row = this.rowPayload(checkbox);
      if (row?.id != null) ids.add(String(row.id));
    });
    return ids;
  }

  isRowSelected(id) {
    this.ensureSelectionState();
    if (this.selectAllMode) return !this.excludedIds.has(id);
    return this.selectedRows.has(id);
  }

  syncRowCheckboxStates() {
    this.ensureSelectionState();
    this.rowCheckboxTargets.forEach((checkbox) => {
      const row = this.rowPayload(checkbox);
      if (!row) return;
      checkbox.checked = this.isRowSelected(String(row.id));
    });
  }

  selectionCount() {
    this.ensureSelectionState();
    if (this.selectAllMode) {
      return Math.max(this.readTotalCount() - this.excludedIds.size, 0);
    }
    return this.selectedRows.size;
  }

  readTotalCount() {
    if (this.hasTotalCountTarget) {
      const fromDom = Number.parseInt(
        this.totalCountTarget.dataset.totalCount,
        10,
      );
      if (Number.isFinite(fromDom) && fromDom >= 0) return fromDom;
    }
    return this.rowCheckboxTargets.length;
  }

  clearSelection() {
    this.ensureSelectionState();
    this.selectAllMode = false;
    this.excludedIds.clear();
    this.selectedRows.clear();
  }

  beginWait() {
    this.exporting = true;
    beginSkeletonWait(this.element);
    this.syncExportButton();
  }

  endWait() {
    if (!this.exporting) return;

    this.exporting = false;
    this.maybeEndSkeletonWait();
    this.syncExportButton();
  }

  maybeEndSkeletonWait() {
    if (!this.exporting && !this.tableBusy) {
      endSkeletonWait(this.element);
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  rowPayload(checkbox) {
    const raw = checkbox.dataset.row;
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  ensureSelectionState() {
    if (!this.selectedRows) this.selectedRows = new Map();
    if (!this.excludedIds) this.excludedIds = new Set();
    if (this.selectAllMode == null) this.selectAllMode = false;
  }

  modalController() {
    if (!this.hasExportModalTarget) return null;

    return this.application.getControllerForElementAndIdentifier(
      this.exportModalTarget,
      "railsui-modal",
    );
  }

  announce(message) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = "";
    requestAnimationFrame(() => {
      this.statusTarget.textContent = message;
    });
  }
}
